import React, { useEffect, useState } from "react";
import { Box, Button, CardContent, CardActions, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { ArrowBackRounded, DownloadRounded, VisibilityRounded, AddRounded } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getNotifications } from "../../../services/notifications/notifications";
import { FilterDialog } from "./notifications-filters";
import { labels, scheduledLabels, moduleTypes } from "../constants/notification-constants";
import FilterIcon from "../../../assets/filter_list.svg";
import SortIcon from "../../../assets/sort.svg";
import Loading from "../../../components/loading/loading";
import { ToolbarButton } from "../../../components/toolbar_button/toolbar_button";
import OrgSelector from "../../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../../store/use-selected-org-store";
import { black, green, greenLight, purple } from "../../../theme/colors";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../users/components/table_toolkit/table_toolkit";
import SortNotificationsPopup, {
  DEFAULT_NOTIFICATIONS_SORT_OPTION,
  NotificationsSortOption,
  notificationsSortOptionLabel,
  notificationsSortOptionToSortOrder,
} from "./sort_notifications_popup";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function statusChipColors(status?: string): { bg: string; color: string } {
  switch (status) {
    case "completed":
      return { bg: "#E2F0D9", color: "#2E7D32" };
    case "inprogress":
      return { bg: "#FFF4CC", color: "#B26A00" };
    case "scheduled":
      return { bg: "#E3F2FD", color: "#1976d2" };
    case "cancelled":
      return { bg: "#ECECEC", color: "#7C797C" };
    case "failed":
      return { bg: "#FCE4E4", color: "#C62828" };
    default:
      return { bg: "#ECECEC", color: black };
  }
}

export const NotificationList = ({ isScheduledView = false }: { isScheduledView?: boolean }) => {
  const columnLabels = isScheduledView ? scheduledLabels : labels;
  const navigate = useNavigate();
  const [searchParams, setUrlParam] = useSearchParams();
  const [data, setData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [filterDialogOpen, setFilterDialogOpen] = useState<boolean>(false);
  const [sortDialogOpen, setSortDialogOpen] = useState<boolean>(false);
  const [sortOption, setSortOption] = useState<NotificationsSortOption>(DEFAULT_NOTIFICATIONS_SORT_OPTION);
  const [selectedModule, setSelectedModule] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { selectedOrgId } = useSelectedOrgStore();

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    module: "",
  });

  const activeFilterCount = [filters.module, filters.startDate && filters.endDate].filter(Boolean).length;

  const [paginationModel, setPaginationModel] = useState({
    pageSize: ROWS_PER_PAGE_OPTIONS[0],
    page: searchParams.has("page")
      ? parseInt(searchParams.get("page")!)
      : 1,
  });

  const handleClearFilters = () => {
    setSelectedModule("");
    setStartDate("");
    setEndDate("");
    setFilters({ startDate: '', endDate: '', module: '' })
    setPaginationModel((prev) => ({ ...prev, page: 1 }));
    setFilterDialogOpen(false);
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPaginationModel({ ...paginationModel, page: newPage + 1 });
    setUrlParam({
      page: (newPage + 1).toString()
    }, { replace: true })
  };

  const handleBackClick = () => {
    const currPage = Number(searchParams.get("page")) || 1;

    if (currPage > 1) {
      const newPage = currPage - 1;
      setPaginationModel((prev) => ({ ...prev, page: newPage }))
      setUrlParam(prev => {
        const params = new URLSearchParams(prev);
        params.set("page", (newPage).toString());
        return params;
      }, { replace: true });

    }
  };
  const downloadReport = (reportUrl: string) => {
    window.open(reportUrl, "_blank");
  }

  const handleApplyFilters = () => {
    setPaginationModel((prev) => ({ ...prev, page: 1 }));
    setFilters({
      startDate,
      endDate,
      module: selectedModule
    })
    setFilterDialogOpen(false);
  };

  const startRange = (paginationModel.page - 1) * paginationModel.pageSize + 1;
  const endRange = Math.min(
    (paginationModel.page) * paginationModel.pageSize,
    data?.total || 0
  );
  const customText = `Showing ${data?.total ? startRange : 0} to ${endRange || 0} of ${data?.total || 0} entries`;

  useEffect(() => {
    setIsLoading(true);
    getNotifications({
      ...paginationModel,
      module: filters.module,
      startDate: filters.startDate,
      endDate: filters.endDate,
      isScheduled: isScheduledView,
      sortOrder: notificationsSortOptionToSortOrder(sortOption),
      orgId: selectedOrgId,
    })
      .then((res) => {
        setData(res?.data?.data);
      })
      .catch((error) => {
        console.log("error", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [paginationModel, filters, isScheduledView, sortOption, selectedOrgId]);

  const list = data?.list || [];

  return (
    <>
      <Box flexDirection="row" display="flex" alignItems="center" sx={{ px: 3, pt: 2.5 }}>
        {paginationModel.page > 1 && (
          <IconButton onClick={handleBackClick} sx={{ mr: 0.5 }}>
            <ArrowBackRounded />
          </IconButton>
        )}

        <Typography variant="h6" fontWeight={700} color={black}>
          {isScheduledView ? "Scheduled Notifications" : "View Notifications"}
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
          gap={1.5}
          mb={2}
        >
          <OrgSelector lockToOwnOrg />
          <ToolbarButton
            icon={SortIcon}
            label={notificationsSortOptionLabel(sortOption)}
            onClick={() => setSortDialogOpen(true)}
          />
          <ToolbarButton
            icon={FilterIcon}
            label="Filters"
            badgeCount={activeFilterCount}
            onClick={() => setFilterDialogOpen(true)}
          />
          <Button
            onClick={() =>
              navigate(
                "/notifications/add-notification",
                isScheduledView ? { state: { isScheduled: true } } : undefined
              )
            }
            variant="contained"
            disableElevation
            startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
            sx={{
              bgcolor: purple,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#7A2074" },
            }}
          >
            {isScheduledView ? "Schedule Notification" : "Add Notification"}
          </Button>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loading />
          </Box>
        ) : list.length === 0 ? (
          <TableEmptyState
            message="No notifications found"
            hint="Try adjusting your search or filters."
          />
        ) : (
          <Box mt={1}>
            <TableHeaderRow
              labels={columnLabels.map((item) => ({ label: item.label }))}
              lastColumnWidth={110}
              align="left"
              bordered
            />
            {list.map((item: any, index: number) => (
              <TableRow
                key={item.id}
                bordered
                onClick={() => {
                  navigate(`/notifications/view-notification/${item.id}`, {
                    state: { notificationData: item },
                  });
                }}
              >
                <TableCell label="Title" bordered>
                  <Tooltip title={item?.contents?.["content_en"]?.["title"] || "-"} placement="top" arrow>
                    <Typography fontSize={14} color={black} noWrap textOverflow="ellipsis" overflow="hidden">
                      {item?.contents?.["content_en"]?.["title"] || "-"}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell label="Module" bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {item?.module || "-"}
                  </Typography>
                </TableCell>
                <TableCell label="Campaign ID" bordered>
                  <Typography fontSize={14} color={black} noWrap>
                    {item?.campaign_id ?? "-"}
                  </Typography>
                </TableCell>
                {isScheduledView ? (
                  <>
                    <TableCell label="Created At" bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item?.createdAt ? new Date(item.createdAt).toLocaleString() : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Scheduled At" bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item?.scheduled_at ? new Date(item.scheduled_at).toLocaleString() : "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Total Uploaded" bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item?.totalUploaded?.toString() ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Status" bordered>
                      <Chip
                        label={item?.status?.toString()?.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: statusChipColors(item?.status).bg,
                          color: statusChipColors(item?.status).color,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell label="Success" bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item?.success?.toString() ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Failed" bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item?.failure?.toString() ?? "-"}
                      </Typography>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell label="Created At" bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item?.createdAt?.split("T")[0] || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Total Uploaded" bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item?.totalUploaded?.toString() ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Total Sent" bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item?.totalSent?.toString() ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Status" bordered>
                      <Chip
                        label={item?.status?.toString()?.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: statusChipColors(item?.status).bg,
                          color: statusChipColors(item?.status).color,
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell label="Success" bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item?.success?.toString() ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Failed" bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item?.failure?.toString() ?? "-"}
                      </Typography>
                    </TableCell>
                  </>
                )}
                <TableCell isLast lastColumnWidth={110} bordered>
                  <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} gap={0.5}>
                    <Tooltip title="View notification">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/notifications/view-notification/${item.id}`, {
                            state: { notificationData: item },
                          });
                        }}
                        sx={{ bgcolor: greenLight, "&:hover": { bgcolor: "#D6EEE0" } }}
                      >
                        <VisibilityRounded sx={{ color: green, fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download report">
                      <span>
                        <IconButton
                          disabled={!item.report_url}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            downloadReport(item.report_url);
                          }}
                          sx={{ bgcolor: "#EDE7F6", "&:hover": { bgcolor: "#E0D4F2" } }}
                        >
                          <DownloadRounded sx={{ color: purple, fontSize: 20 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </Box>
        )}
      </CardContent>

      {list.length > 0 && (
        <CardActions sx={{ px: 3, pb: 3, mt: "auto" }}>
          <Box width="100%">
            <TableFooterPagination
              count={data?.total || 0}
              page={paginationModel.page - 1}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={paginationModel.pageSize}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={(newSize) => {
                setPaginationModel({ page: 1, pageSize: newSize });
              }}
            />
          </Box>
        </CardActions>
      )}

      <FilterDialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        startDate={startDate}
        endDate={endDate}
        selectedModule={selectedModule}
        moduleOptions={moduleTypes}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        setSelectedModule={setSelectedModule}
      />

      <SortNotificationsPopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />
    </>
  );
};
